import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Users, 
  FileText, 
  TrendingUp, 
  Clock, 
  AlertTriangle, 
  Building2, 
  CheckCircle, 
  XCircle,
  Globe,
  Shield,
  Settings,
  BarChart3,
  RotateCcw,
  Filter
} from 'lucide-react';
import ChartThemeToggle from '@/components/dashboard/ChartThemeToggle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import RoleSwitcher from '@/components/admin/RoleSwitcher';
import WelcomeBanner from '@/components/dashboard/WelcomeBanner';
import QuickStatsWidget, { StatItem } from '@/components/dashboard/QuickStatsWidget';
import CostSavingsSummaryWidget from '@/components/admin/CostSavingsSummaryWidget';
import BillingBasedSavingsWidget from '@/components/admin/BillingBasedSavingsWidget';
import AgencyKpiWidget from '@/components/admin/AgencyKpiWidget';
import NotificationCenter from '@/components/dashboard/NotificationCenter';
import PatientRiskTrendChart from '@/components/admin/PatientRiskTrendChart';
import CumulativeBillableHoursChart from '@/components/admin/CumulativeBillableHoursChart';
import CareUtilizationMixChart from '@/components/admin/CareUtilizationMixChart';
import GoalStatusWidget from '@/components/dashboard/GoalStatusWidget';
import DateRangeFilter from '@/components/dashboard/DateRangeFilter';
import DashboardExport from '@/components/dashboard/DashboardExport';
import { useDashboardTrends } from '@/hooks/useTrendCalculator';
import { usePersistedTrendPeriod } from '@/hooks/usePersistedTrendPeriod';
import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import { useGoalThresholds } from '@/hooks/useGoalThresholds';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface DashboardStats {
  totalPatients: number;
  totalSessions: number;
  completedSessions: number;
  recentSessions: number;
  avgResponseTime: number;
  redFlagCases: number;
  totalAgencies: number;
  activeAgencies: number;
}

interface PendingAgency {
  id: string;
  name: string;
  code: string;
  contact_email: string | null;
  created_at: string;
}

interface Agency {
  id: string;
  name: string;
  code: string;
}

export default function SuperAdminDashboardPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [trendPeriod, setTrendPeriod] = usePersistedTrendPeriod('week');
  const { trends } = useDashboardTrends({ period: trendPeriod });
  const { thresholds } = useGoalThresholds(undefined, trendPeriod);
  const [stats, setStats] = useState<DashboardStats>({
    totalPatients: 0,
    totalSessions: 0,
    completedSessions: 0,
    recentSessions: 0,
    avgResponseTime: 0,
    redFlagCases: 0,
    totalAgencies: 0,
    activeAgencies: 0
  });
  const [pendingAgencies, setPendingAgencies] = useState<PendingAgency[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(() => {
    const saved = localStorage.getItem('super-admin-selected-agency');
    return saved || null;
  });

  // Persist selected agency to localStorage
  useEffect(() => {
    if (selectedAgencyId) {
      localStorage.setItem('super-admin-selected-agency', selectedAgencyId);
    } else {
      localStorage.removeItem('super-admin-selected-agency');
    }
  }, [selectedAgencyId]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingAgency, setRejectingAgency] = useState<PendingAgency | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [dateRange, setDateRange] = usePersistedDateRange();
  const analyticsContainerRef = useRef<HTMLDivElement>(null);
  const costSavingsRef = useRef<HTMLDivElement>(null);
  const billingRef = useRef<HTMLDivElement>(null);
  const riskTrendRef = useRef<HTMLDivElement>(null);
  const billableHoursRef = useRef<HTMLDivElement>(null);
  const careUtilizationRef = useRef<HTMLDivElement>(null);

  const hasActiveFilters = dateRange.from !== undefined || dateRange.to !== undefined || trendPeriod !== 'week' || selectedAgencyId !== null;
  
  const handleResetFilters = useCallback(() => {
    setDateRange({ from: undefined, to: undefined });
    setTrendPeriod('week');
    setSelectedAgencyId(null);
  }, [setDateRange, setTrendPeriod]);

  const triggerUpdateAnimation = () => {
    setIsUpdating(true);
    setTimeout(() => setIsUpdating(false), 1500);
  };

  useEffect(() => {
    loadDashboardStats();
    loadPendingAgencies();
    loadAgencies();

    const channel = supabase
      .channel('super-admin-dashboard-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'triage_sessions' }, () => {
        triggerUpdateAnimation();
        loadDashboardStats();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'patients' }, () => {
        triggerUpdateAnimation();
        loadDashboardStats();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agencies' }, () => {
        triggerUpdateAnimation();
        loadDashboardStats();
        loadPendingAgencies();
        loadAgencies();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code')
        .eq('is_approved', true)
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error loading agencies:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      // Platform-wide stats for super admin
      const [
        { count: totalPatients },
        { count: totalSessions },
        { count: completedSessions },
        { count: redFlagCases },
        { count: totalAgencies },
        { count: activeAgencies }
      ] = await Promise.all([
        supabase.from('patients').select('*', { count: 'exact', head: true }),
        supabase.from('triage_sessions').select('*', { count: 'exact', head: true }),
        supabase.from('triage_sessions').select('*', { count: 'exact', head: true }).not('completed_at', 'is', null),
        supabase.from('triage_sessions').select('*', { count: 'exact', head: true }).eq('red_flags', true),
        supabase.from('agencies').select('*', { count: 'exact', head: true }),
        supabase.from('agencies').select('*', { count: 'exact', head: true }).eq('is_approved', true)
      ]);

      // Recent sessions (last 7 days)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { count: recentSessions } = await supabase
        .from('triage_sessions')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sevenDaysAgo);

      // Average response time
      const { data: completedForAvg } = await supabase
        .from('triage_sessions')
        .select('started_at, completed_at')
        .not('completed_at', 'is', null)
        .limit(100);

      let avgResponseTime = 0;
      if (completedForAvg && completedForAvg.length > 0) {
        const totalMinutes = completedForAvg.reduce((sum, s) => {
          const start = new Date(s.started_at).getTime();
          const end = new Date(s.completed_at!).getTime();
          return sum + (end - start) / 60000;
        }, 0);
        avgResponseTime = Math.round((totalMinutes / completedForAvg.length) * 10) / 10;
      }

      setStats({
        totalPatients: totalPatients || 0,
        totalSessions: totalSessions || 0,
        completedSessions: completedSessions || 0,
        recentSessions: recentSessions || 0,
        avgResponseTime,
        redFlagCases: redFlagCases || 0,
        totalAgencies: totalAgencies || 0,
        activeAgencies: activeAgencies || 0
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code, contact_email, created_at')
        .eq('is_approved', false)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPendingAgencies(data || []);
    } catch (error) {
      console.error('Error loading pending agencies:', error);
    }
  };

  const handleApproveAgency = async (agencyId: string) => {
    setApprovingId(agencyId);
    const agency = pendingAgencies.find(a => a.id === agencyId);

    try {
      const { error } = await supabase
        .from('agencies')
        .update({ is_approved: true })
        .eq('id', agencyId);

      if (error) throw error;

      if (agency) {
        await supabase.from('agency_audit_logs').insert({
          agency_id: agencyId,
          agency_name: agency.name,
          agency_code: agency.code,
          action: 'approved',
          actor_user_id: user?.id,
          actor_email: user?.email,
          metadata: { contact_email: agency.contact_email }
        });
      }

      toast.success('Agency approved successfully');
      setPendingAgencies(prev => prev.filter(a => a.id !== agencyId));
    } catch (error) {
      console.error('Error approving agency:', error);
      toast.error('Failed to approve agency');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectAgency = async () => {
    if (!rejectingAgency) return;

    setIsRejecting(true);
    try {
      await supabase.from('agency_audit_logs').insert({
        agency_id: rejectingAgency.id,
        agency_name: rejectingAgency.name,
        agency_code: rejectingAgency.code,
        action: 'rejected',
        reason: rejectionReason || null,
        actor_user_id: user?.id,
        actor_email: user?.email,
        metadata: { contact_email: rejectingAgency.contact_email }
      });

      const { error } = await supabase
        .from('agencies')
        .delete()
        .eq('id', rejectingAgency.id);

      if (error) throw error;

      toast.success(`Agency "${rejectingAgency.name}" has been rejected and removed`);
      setPendingAgencies(prev => prev.filter(a => a.id !== rejectingAgency.id));
    } catch (error) {
      console.error('Error rejecting agency:', error);
      toast.error('Failed to reject agency');
    } finally {
      setIsRejecting(false);
      setRejectingAgency(null);
      setRejectionReason('');
    }
  };

  const statCards: StatItem[] = [
    { 
      title: 'Total Agencies', 
      value: stats.totalAgencies, 
      description: `${stats.activeAgencies} active`, 
      icon: Building2, 
      color: 'text-indigo-600', 
      onClick: () => navigate('/admin/agencies') 
    },
    { 
      title: 'Total Patients', 
      value: stats.totalPatients, 
      description: 'Across all agencies', 
      icon: Users, 
      color: 'text-blue-600', 
      onClick: () => navigate('/admin/patients'),
      trend: trends.patients ? { 
        value: trends.patients.percentChange, 
        isPositive: trends.patients.isPositive, 
        label: trends.patients.label,
        sparklineData: trends.patients.sparklineData,
        threshold: thresholds.patients
      } : undefined
    },
    { 
      title: 'Triage Sessions', 
      value: stats.totalSessions, 
      description: `${stats.completedSessions} completed`, 
      icon: FileText, 
      color: 'text-green-600', 
      onClick: () => navigate('/admin/analytics'),
      trend: trends.sessions ? { 
        value: trends.sessions.percentChange, 
        isPositive: trends.sessions.isPositive, 
        label: trends.sessions.label,
        sparklineData: trends.sessions.sparklineData,
        threshold: thresholds.sessions
      } : undefined
    },
    { 
      title: 'Recent Activity', 
      value: stats.recentSessions, 
      description: 'Last 7 days', 
      icon: TrendingUp, 
      color: 'text-purple-600', 
      onClick: () => navigate('/admin/analytics'),
      trend: trends.recentActivity ? { 
        value: trends.recentActivity.percentChange, 
        isPositive: trends.recentActivity.isPositive, 
        label: trends.recentActivity.label,
        sparklineData: trends.recentActivity.sparklineData,
        threshold: thresholds.sessions
      } : undefined
    },
    { 
      title: 'Avg Response Time', 
      value: `${stats.avgResponseTime.toFixed(1)}min`, 
      description: 'Per assessment', 
      icon: Clock, 
      color: 'text-orange-600', 
      onClick: () => navigate('/admin/analytics') 
    },
    { 
      title: 'Red Flag Cases', 
      value: stats.redFlagCases, 
      description: 'Require attention', 
      icon: AlertTriangle, 
      color: 'text-red-600', 
      onClick: () => navigate('/admin/patients?status=red_flags'),
      trend: trends.redFlags ? { 
        value: trends.redFlags.percentChange, 
        isPositive: trends.redFlags.isPositive, 
        label: trends.redFlags.label,
        sparklineData: trends.redFlags.sparklineData,
        threshold: thresholds.redFlags
      } : undefined
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Welcome Banner */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <WelcomeBanner
              role="super_admin"
              userName={user?.email?.split('@')[0] || 'Admin'}
              userEmail={user?.email}
              quickActions={[
                { label: 'Manage Agencies', icon: Building2, onClick: () => navigate('/admin/agencies') },
                { label: 'View Analytics', icon: BarChart3, onClick: () => navigate('/admin/analytics'), variant: 'outline' },
                { label: 'Settings', icon: Settings, onClick: () => navigate('/admin/settings'), variant: 'outline' },
              ]}
            >
              {isUpdating && (
                <div className="flex items-center gap-2 text-success text-sm">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                  </span>
                  Live update detected
                </div>
              )}
            </WelcomeBanner>
          </div>
          <RoleSwitcher currentView="super_admin" />
        </div>

        {/* Platform Overview Badge */}
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg border border-primary/10">
          <div className="p-3 rounded-full bg-primary/10">
            <Globe className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Platform-Wide Overview</h3>
            <p className="text-sm text-muted-foreground">
              Viewing data across all {stats.totalAgencies} agencies • {stats.totalPatients} patients • {stats.totalSessions} total sessions
            </p>
          </div>
          <Badge variant="outline" className="ml-auto hidden sm:flex">
            <Shield className="h-3 w-3 mr-1" />
            Full Access
          </Badge>
        </div>

        {/* Stats Grid */}
        <QuickStatsWidget 
          stats={statCards} 
          columns={6} 
          isUpdating={isUpdating}
          trendPeriod={trendPeriod}
          onTrendPeriodChange={setTrendPeriod}
        />

        {/* Pending Agencies Section */}
        {pendingAgencies.length > 0 && (
          <Card className="border-warning/50 bg-warning/5">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-warning" />
                <CardTitle>Pending Agency Approvals</CardTitle>
              </div>
              <CardDescription>
                {pendingAgencies.length} agency registration{pendingAgencies.length !== 1 ? 's' : ''} awaiting your review
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingAgencies.map((agency) => (
                  <div key={agency.id} className="flex items-center justify-between p-4 bg-background rounded-lg border">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{agency.name}</span>
                        <Badge variant="outline">{agency.code}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {agency.contact_email} • Registered {formatDistanceToNow(new Date(agency.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectingAgency(agency)}
                        className="text-destructive hover:text-destructive"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApproveAgency(agency.id)}
                        disabled={approvingId === agency.id}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        {approvingId === agency.id ? 'Approving...' : 'Approve'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics Widgets */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-foreground">Analytics Overview</h3>
          <div className="flex flex-wrap items-center gap-2">
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleResetFilters}
                className="h-8 text-gray-500 hover:text-gray-700"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
            <Select
              value={selectedAgencyId || 'all'}
              onValueChange={(value) => setSelectedAgencyId(value === 'all' ? null : value)}
            >
              <SelectTrigger className="w-[200px] h-9">
                <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All Agencies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agencies</SelectItem>
                {agencies.map((agency) => (
                  <SelectItem key={agency.id} value={agency.id}>
                    {agency.name} ({agency.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ChartThemeToggle />
            <DateRangeFilter 
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            <DashboardExport
              stats={{
                ...stats,
                pendingAgencies: pendingAgencies.length,
              }}
              dateRange={dateRange}
              isAllAgencies={selectedAgencyId === null}
              selectedAgencyName={selectedAgencyId ? agencies.find(a => a.id === selectedAgencyId)?.name : undefined}
              analyticsContainerRef={analyticsContainerRef}
              chartRefs={[
                { ref: costSavingsRef, title: 'Cost Savings Summary' },
                { ref: billingRef, title: 'Billing Based Savings' },
                { ref: riskTrendRef, title: 'Patient Risk Trend' },
                { ref: billableHoursRef, title: 'Cumulative Billable Hours' },
                { ref: careUtilizationRef, title: 'Care Utilization Mix' },
              ]}
            />
          </div>
        </div>

        <div ref={analyticsContainerRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div ref={costSavingsRef}>
            <CostSavingsSummaryWidget 
              agencyId={selectedAgencyId || undefined}
              agencyName={selectedAgencyId ? agencies.find(a => a.id === selectedAgencyId)?.name : undefined}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
          <div ref={billingRef}>
            <BillingBasedSavingsWidget 
              agencyId={selectedAgencyId}
              agencyName={selectedAgencyId ? agencies.find(a => a.id === selectedAgencyId)?.name : undefined}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <AgencyKpiWidget agencyId={selectedAgencyId || undefined} />
          <GoalStatusWidget agencyId={selectedAgencyId || undefined} />
          <NotificationCenter role="super_admin" />
        </div>

        {/* New Analytics Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div ref={billableHoursRef}>
            <CumulativeBillableHoursChart 
              agencyId={selectedAgencyId}
              agencyName={selectedAgencyId ? agencies.find(a => a.id === selectedAgencyId)?.name : undefined}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
          <div ref={careUtilizationRef}>
            <CareUtilizationMixChart 
              agencyId={selectedAgencyId || undefined}
              agencyName={selectedAgencyId ? agencies.find(a => a.id === selectedAgencyId)?.name : undefined}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
        </div>

        {/* Patient Risk Trend */}
        <div ref={riskTrendRef}>
          <PatientRiskTrendChart 
            agencyId={selectedAgencyId || undefined}
            agencyName={selectedAgencyId ? agencies.find(a => a.id === selectedAgencyId)?.name : undefined}
            startDate={dateRange.from}
            endDate={dateRange.to}
          />
        </div>

        {/* Rejection Dialog */}
        <AlertDialog open={!!rejectingAgency} onOpenChange={() => setRejectingAgency(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reject Agency Registration</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to reject "{rejectingAgency?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Reason (optional)</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Provide a reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRejectAgency}
                disabled={isRejecting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isRejecting ? 'Rejecting...' : 'Reject Agency'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
